import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import ControlPanelLayout from "./ControlPanelLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AppWindow, Calendar, CheckCircle2, ArrowRight, Activity, Sparkles, Clock3 } from "lucide-react";

export default function CPDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ apps: 0, verified: 0, meetings: 0, upcoming: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [{ data: apps }, { data: meetings }] = await Promise.all([
        supabase.from("apps").select("id, verified_at").eq("owner_id", user.id),
        supabase.from("meetings").select("id, scheduled_start, is_active").eq("host_id", user.id),
      ]);
      const now = new Date();
      setStats({
        apps: apps?.length || 0,
        verified: apps?.filter((a) => a.verified_at).length || 0,
        meetings: meetings?.length || 0,
        upcoming:
          meetings?.filter((m) => m.scheduled_start && new Date(m.scheduled_start) > now).length ||
          0,
      });
      setLoading(false);
    })();
  }, [user]);

  return (
    <ControlPanelLayout>
      <div className="space-y-6">
        <section className="rounded-3xl border border-border/60 bg-[linear-gradient(135deg,hsl(var(--background)),hsl(var(--primary)/0.08))] p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Workspace overview</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight">Everything you need to run your meeting workspace.</h2>
              <p className="mt-3 text-muted-foreground">
                Review app health, API readiness, and meeting volume from one place before you jump into day-to-day actions.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild>
                <Link to="/control-panel/apps">
                  Manage apps <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/control-panel/meetings">Plan meetings</Link>
              </Button>
            </div>
          </div>
        </section>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label="Apps"
            helper="Connected workspaces"
            value={loading ? "..." : stats.apps}
            icon={AppWindow}
          />
          <StatCard
            label="Verified keys"
            helper="Ready for production requests"
            value={loading ? "..." : stats.verified}
            icon={CheckCircle2}
          />
          <StatCard
            label="Meetings"
            helper="Total hosted sessions"
            value={loading ? "..." : stats.meetings}
            icon={Calendar}
          />
          <StatCard
            label="Upcoming"
            helper="Scheduled ahead"
            value={loading ? "..." : stats.upcoming}
            icon={Clock3}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
          <Card className="border-border/60 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-4 w-4 text-primary" />
                Recommended next steps
              </CardTitle>
              <CardDescription>Use this as the cleanest path to get a new workspace live.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Step n={1} text="Create your app and generate an API key." />
              <Step n={2} text="Verify the key so your integration is confirmed end-to-end." />
              <Step n={3} text="Create a meeting and test the host or guest join flow." />
              <Step n={4} text="Review settings and naming before sharing with users." />
            </CardContent>
          </Card>

          <Card className="border-border/60 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Activity className="h-4 w-4 text-primary" />
                Quick actions
              </CardTitle>
              <CardDescription>Jump straight into the areas most teams use every day.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Button asChild variant="outline" className="justify-between">
                <Link to="/control-panel/apps">
                  Create or rotate an API key
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="justify-between">
                <Link to="/control-panel/meetings">
                  Schedule a new meeting
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="justify-between">
                <Link to="/control-panel/settings">
                  Update workspace identity
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </ControlPanelLayout>
  );
}

function StatCard({
  label,
  helper,
  value,
  icon: Icon,
}: {
  label: string;
  helper: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card className="border-border/60 shadow-sm">
      <CardContent className="flex items-start justify-between gap-4 p-5">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <p className="mt-2 text-3xl font-semibold tracking-tight">{value}</p>
          <p className="mt-1 text-xs text-muted-foreground">{helper}</p>
        </div>
        <div className="rounded-2xl bg-primary/10 p-3 text-primary">
          <Icon className="h-5 w-5" />
        </div>
      </CardContent>
    </Card>
  );
}

function Step({ n, text }: { n: number; text: string }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-border/50 bg-muted/20 p-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
        {n}
      </span>
      <p className="text-sm leading-6">{text}</p>
    </div>
  );
}
